import { useNuxtApp } from 'nuxt/app';
import { camelToSnakeCase } from '~~/common/utils';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';

type Awt = PostTranslationPluginProvides['awt'];

/** Only the parts of an address these formatters read. */
interface FormattableAddress {
  postCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  streetName?: string | null;
  streetType?: string | null;
  streetNo?: string | null;
  building?: string | null;
  staircase?: string | null;
  floor?: string | null;
  door?: string | null;
}

interface FormattableCountry {
  countryId?: string | null;
  name?: string | null;
}

export function formatAddress (address?: FormattableAddress | null) {
  return [
    [address?.postCode, address?.city].filter(Boolean).join(' '),
    formatAddressLine(address),
  ].filter(Boolean).join(', ');
}

export function formatAddressWithCountry (address?: FormattableAddress | null, countries?: FormattableCountry[] | null) {
  const countryName = countries?.find((country) => country.countryId === address?.countryCode)?.name ?? '';
  return [
    countryName,
    [address?.postCode, address?.city].filter(Boolean).join(' '),
    formatAddressLine(address),
  ].filter(Boolean).join(', ');
}

export function formatAddressLine (address?: FormattableAddress | null, $awt: Awt = useNuxtApp().$awt) {
  let str = '';
  const format = addressTokenizer.bind(null, $awt, address);
  for (const key of [
    'streetName', // aw.address.street_name
    'streetType', // aw.address.street_type
    'streetNo', // aw.address.street_no
    'building', // aw.address.building
    'staircase', // aw.address.staircase
    'floor', // aw.address.floor
    'door', // aw.address.door
  ] as const) {
    str += ' ' + format(key);
  }

  if (address?.floor && Number.isNaN(Number.parseInt(address?.floor))) {
    str = str.replace($awt('aw.address.floor', { floor: '' }), '');
  }

  str = str.trim().replace(/\s+/g, ' ');
  return str;
}

function addressTokenizer ($awt: Awt, address: FormattableAddress | null | undefined, key: keyof FormattableAddress) {
  return address?.[key] ? $awt(`aw.address.${camelToSnakeCase(key)}`, { [key]: address[key] }) : '';
}
